import React, { useEffect, useMemo, useState } from 'react';
import useAutocomplete from '@mui/material/useAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import _ from 'lodash';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CloseOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import {
    BRAND_MODAL_ACCENT_CHIP_BG,
    BRAND_MODAL_ACCENT_CHIP_BORDER,
    BRAND_MODAL_ACCENT_ICON,
    BRAND_MODAL_ACCENT_LIST_SELECTED,
    BRAND_MODAL_ACCENT,
    BRAND_OUTLINE_VARIANT,
} from '../../pages/BrandDashboard/brandDashboardTokens';

const Root = styled('div')<{
    themeStyle: 'dark' | 'light';
}>(
    ({ themeStyle }) => `
  color: ${theme[themeStyle].tag_white};
  font-size: 14px;
`,
);

const InputWrapper = styled('div')<{
    themeStyle: 'dark' | 'light';
}>(
    ({ themeStyle }) => `
  width: 100%;
  border: 1px solid ${theme[themeStyle].tag_white};
  background-color: ${theme[themeStyle].gray_text};
  border-radius: 4px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;

  &:hover {
    border-color: ${theme[themeStyle].primary};
  }

  &.focused {
    border-color: ${theme[themeStyle].primary};
    box-shadow: 0 0 0 2px ${theme[themeStyle].primary};
  }

  & input {
    background-color: ${theme[themeStyle].gray_text};
    color: ${theme[themeStyle].tag_white};
    height: 50px;
    box-sizing: border-box;
    padding: 10px 12px;
    width: 0;
    min-width: 44px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
    font-size: 1.1rem;
  }
`,
);

const StyledTag = styled('div')<{
    themeStyle: 'dark' | 'light';
}>(
    ({ themeStyle }) => `
  display: flex;
  align-items: center;
  height: 36px;
  margin: 4px;
  line-height: 34px;
  background-color: ${theme[themeStyle].primary_background};
  border: 1px solid ${theme[themeStyle].tag_white};
  border-radius: 4px;
  box-sizing: content-box;
  padding: 0 10px 0 16px;
  outline: 0;
  overflow: hidden;
  font-size: 1.05rem;

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 18px;
    cursor: pointer;
    padding: 0px;
    color: ${theme[themeStyle].tag_white};
  }
`,
);

const Listbox = styled('ul')<{ themeStyle: 'dark' | 'light' }>`
    max-width: 300px;
    margin: 2px 0 0;
    padding: 0;
    position: absolute;
    list-style: none;
    background-color: ${({ themeStyle }) => theme[themeStyle].primary_background};
    overflow: auto;
    max-height: 250px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgb(0 0 0 / 0.15);
    z-index: 1;

    & li {
        padding: 5px 12px;
        display: flex;

        & span {
            flex-grow: 1;
            color: ${({ themeStyle }) => theme[themeStyle].tag_white};
        }

        & svg {
            color: transparent;
        }
    }

    & li[aria-selected='true'] {
        background-color: ${({ themeStyle }) => theme[themeStyle].primary};
        font-weight: 600;

        & svg {
            color: ${({ themeStyle }) => theme[themeStyle].tag_white};
        }
    }

    & li.${autocompleteClasses.focused} {
        background-color: ${({ themeStyle }) => theme[themeStyle].highlight};
        cursor: pointer;

        & svg {
            color: currentColor;
        }
    }
`;

interface AutocompleteProps {
    options: string[];
    defaultValue?: string[];
    onChange?: (value: string[]) => void;
    maxSelectable?: number;
    initialValue: string[];
    /** Borderless dark field + gold focus (brand system forms). */
    brandSurface?: boolean;
}

const CustomizedAutocomplete: React.FC<AutocompleteProps> = ({
    options = [],
    defaultValue = [],
    onChange,
    maxSelectable,
    initialValue = [],
    brandSurface = false,
}) => {
    const safeInitialValue = useMemo(() => (Array.isArray(initialValue) ? initialValue : []), [initialValue]);
    const [initValue, setInitialValue] = useState<string[]>(safeInitialValue);
    const { themeStyle } = useThemeContext();

    const {
        getRootProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value = [], // Ensure value is an array
        focused,
        setAnchorEl,
    } = useAutocomplete({
        multiple: true,
        options,
        defaultValue,
        value: initValue, // Ensure value is tied to state
        isOptionEqualToValue: (option, val) => option === val && val !== undefined, // Ensure correct value matching
        onChange: (_, newValue) => {
            if (maxSelectable && newValue.length > maxSelectable) return;
            setInitialValue(Array.isArray(newValue) ? newValue : []);
            onChange?.(Array.isArray(newValue) ? newValue : []);
        },
    });

    // Ensure initialValue syncs properly without unnecessary updates
    useEffect(() => {
        if (!_.isEqual(initValue, safeInitialValue)) {
            setInitialValue(safeInitialValue);
        }
    }, [safeInitialValue, initValue]);

    // Ensure value syncs properly without unnecessary updates
    useEffect(() => {
        if (!_.isEqual(initValue, value) && value) {
            setInitialValue(Array.isArray(value) ? value : []);
        }
    }, [value, initValue]);

    const inputShell = brandSurface ? (
        <Box
            ref={setAnchorEl}
            className={focused ? 'focused' : ''}
            sx={{
                width: '100%',
                border: `1px solid ${focused ? BRAND_MODAL_ACCENT : BRAND_OUTLINE_VARIANT}`,
                bgcolor: '#262626',
                borderRadius: '12px',
                px: 1.5,
                py: 0.75,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                boxSizing: 'border-box',
                overflow: 'hidden',
                transition: 'border-color 0.15s ease',
                '& input': {
                    bgcolor: '#262626',
                    color: '#f2f2f6',
                    height: 50,
                    boxSizing: 'border-box',
                    padding: '10px 16px',
                    width: 0,
                    minWidth: 44,
                    flexGrow: 1,
                    border: 0,
                    margin: 0,
                    outline: 0,
                    fontSize: '1.05rem',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: 'none',
                    boxShadow: 'none',
                    borderRadius: 0,
                    borderBottom: 'none',
                    '&:focus': {
                        outline: 'none',
                        border: 'none',
                        boxShadow: 'none',
                        backgroundImage: 'none',
                    },
                },
            }}>
            {initValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                    <Box
                        key={key}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 36,
                            m: '4px',
                            lineHeight: '34px',
                            bgcolor: BRAND_MODAL_ACCENT_CHIP_BG,
                            border: `1px solid ${BRAND_MODAL_ACCENT_CHIP_BORDER}`,
                            borderRadius: '4px',
                            boxSizing: 'content-box',
                            px: '10px',
                            pl: '16px',
                            outline: 0,
                            overflow: 'hidden',
                            fontSize: '1.05rem',
                            '& span': {
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                textTransform: 'capitalize',
                            },
                        }}>
                        <span>{option}</span>
                        <CloseOutlined
                            onClick={tagProps.onDelete}
                            sx={{
                                width: '16px',
                                height: '16px',
                                ml: 0.5,
                                color: 'rgba(255,255,255,0.85)',
                                cursor: 'pointer',
                            }}
                        />
                    </Box>
                );
            })}
            <input {...getInputProps()} />
        </Box>
    ) : (
        <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''} themeStyle={themeStyle}>
            {initValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                    <StyledTag key={key} themeStyle={themeStyle}>
                        <span>{option}</span>
                        <CloseOutlined
                            onClick={tagProps.onDelete}
                            sx={{
                                width: '16px',
                                height: '16px',
                            }}
                        />
                    </StyledTag>
                );
            })}
            <input {...getInputProps()} />
        </InputWrapper>
    );

    return (
        <Root themeStyle={themeStyle}>
            <div {...getRootProps()}>
                {inputShell}
                <Box sx={{ justifyContent: 'flex-end', display: 'flex' }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 8,
                            color: brandSurface ? 'rgba(255,255,255,0.45)' : theme[themeStyle].gray_message,
                            fontStyle: 'italic',
                            mt: 2,
                        }}>
                        Select {maxSelectable} max.
                    </Typography>
                </Box>
            </div>
            {groupedOptions.length > 0 && (
                <Listbox
                    sx={{
                        zIndex: 99999,
                        ...(brandSurface
                            ? {
                                  backgroundColor: '#262626',
                                  border: '1px solid #484847',
                                  [`& li.${autocompleteClasses.focused}`]: {
                                      backgroundColor: 'rgba(255,255,255,0.06) !important',
                                  },
                                  '& li[aria-selected="true"]': {
                                      backgroundColor: `${BRAND_MODAL_ACCENT_LIST_SELECTED} !important`,
                                  },
                                  '& li[aria-selected="true"] svg': {
                                      color: `${BRAND_MODAL_ACCENT_ICON} !important`,
                                  },
                              }
                            : {}),
                    }}
                    {...getListboxProps()}
                    themeStyle={themeStyle}>
                    {groupedOptions.map((option, index) => {
                        const optionLabel: any =
                            typeof option === 'string' ? option : 'label' in option ? option.label : option;
                        const { ...optionProps } = getOptionProps({ option: optionLabel, index });

                        return (
                            <li
                                {...optionProps}
                                key={index}
                                style={{
                                    pointerEvents: maxSelectable && initValue.length >= maxSelectable ? 'none' : 'auto',
                                    opacity: maxSelectable && initValue.length >= maxSelectable ? 0.5 : 1,
                                }}>
                                <span
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        textTransform: 'capitalize',
                                    }}>
                                    {optionLabel}
                                </span>
                                <CheckIcon fontSize="small" />
                            </li>
                        );
                    })}
                </Listbox>
            )}
        </Root>
    );
};

export default CustomizedAutocomplete;
